import React from 'react';
import { DatePicker } from '@/components/common/DatePicker';
import { FormField, Input, TextArea } from '@/components/common/FormField';
import * as jobsApi from '@/lib/api/jobs';
import * as materialsApi from '@/lib/api/materials';
import { useAuth } from '@/lib/hooks/useAuth';

interface AddMaterialModalProps {
  jobId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddMaterialModal: React.FC<AddMaterialModalProps> = ({
  jobId,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [materials, setMaterials] = React.useState<any[]>([]);
  const [loadingMaterials, setLoadingMaterials] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  const [formData, setFormData] = React.useState({
    material_id: '',
    quantity_used: '',
    unit: '',
    cost_per_unit: '',
    date_used: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const loadMaterials = async () => {
      try {
        const data = await materialsApi.getMaterials();
        setMaterials(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load materials:', err);
      } finally {
        setLoadingMaterials(false);
      }
    };
    loadMaterials();
  }, []);

  const selectedMaterial = materials.find(m => m.id === parseInt(formData.material_id));

  const totalCost = React.useMemo(() => {
    const qty = parseFloat(formData.quantity_used) || 0;
    const cost = parseFloat(formData.cost_per_unit) || 0;
    return qty * cost;
  }, [formData.quantity_used, formData.cost_per_unit]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.material_id) {
      newErrors.material_id = 'Please select a material';
    }
    if (!formData.quantity_used || parseFloat(formData.quantity_used) <= 0) {
      newErrors.quantity_used = 'Quantity must be greater than 0';
    }
    if (!formData.unit) {
      newErrors.unit = 'Unit is required';
    }
    if (formData.cost_per_unit && parseFloat(formData.cost_per_unit) < 0) {
      newErrors.cost_per_unit = 'Cost cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setSubmitting(true);
    setError('');

    try {
      await jobsApi.addJobMaterial(jobId, {
        material_id: parseInt(formData.material_id),
        quantity_used: parseFloat(formData.quantity_used),
        unit: formData.unit,
        cost_per_unit: formData.cost_per_unit ? parseFloat(formData.cost_per_unit) : null,
        date_used: formData.date_used,
        notes: formData.notes,
        user_id: user?.id,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add material');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMaterialChange = (materialId: string) => {
    const material = materials.find(m => m.id === parseInt(materialId));
    setFormData(prev => ({
      ...prev,
      material_id: materialId,
      unit: material?.unit || prev.unit,
      cost_per_unit: material?.unit_cost?.toString() || prev.cost_per_unit,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a2332] rounded-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-blue-400">inventory_2</span>
            <h3 className="text-xl font-semibold text-white">Add Material</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-400">
                <span className="material-symbols-outlined">error</span>
                <span>{error}</span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Material Selector */}
            <FormField
              label="Material"
              required
              error={errors.material_id}
            >
              <div className="relative">
                <select
                  value={formData.material_id}
                  onChange={(e) => handleMaterialChange(e.target.value)}
                  disabled={loadingMaterials || submitting}
                  className="w-full px-4 py-2.5 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 hover:border-gray-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 appearance-none cursor-pointer disabled:opacity-50"
                >
                  <option value="">Select a material...</option>
                  {materials.map((material) => (
                    <option key={material.id} value={material.id}>
                      {material.name} - {material.category}
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  expand_more
                </span>
              </div>
            </FormField>

            {/* Selected Material Info */}
            {selectedMaterial && (
              <div className="p-3 bg-[#101922] rounded-lg border border-gray-700">
                <p className="text-sm text-gray-400">{selectedMaterial.description}</p>
                {selectedMaterial.supplier && (
                  <p className="text-xs text-gray-500 mt-1">
                    Supplier: {selectedMaterial.supplier}
                  </p>
                )}
              </div>
            )}

            {/* Quantity and Unit */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Quantity Used"
                required
                error={errors.quantity_used}
              >
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.quantity_used}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity_used: e.target.value }))}
                  placeholder="0.00"
                  disabled={submitting}
                  error={!!errors.quantity_used}
                />
              </FormField>

              <FormField
                label="Unit"
                required
                error={errors.unit}
              >
                <Input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                  placeholder="e.g., sq ft, lbs, gallons"
                  disabled={submitting}
                  error={!!errors.unit}
                />
              </FormField>
            </div>

            {/* Cost per Unit */}
            <FormField
              label="Cost per Unit"
              hint="Optional - will calculate total cost"
              error={errors.cost_per_unit}
            >
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost_per_unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, cost_per_unit: e.target.value }))}
                  placeholder="0.00"
                  disabled={submitting}
                  error={!!errors.cost_per_unit}
                  className="pl-8"
                />
              </div>
            </FormField>

            {/* Total Cost Display */}
            {totalCost > 0 && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-400">Total Cost:</span>
                  <span className="text-lg font-bold text-blue-400">
                    ${totalCost.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Date Used */}
            <DatePicker
              label="Date Used"
              value={formData.date_used}
              onChange={(date) => setFormData(prev => ({ 
                ...prev, 
                date_used: typeof date === 'string' ? date : date?.toISOString().split('T')[0] || ''
              }))}
              disabled={submitting}
              useReactDatePicker={false}
            />

            {/* Notes */}
            <FormField
              label="Notes"
              hint="Optional - add any additional context"
            >
              <TextArea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes about this material usage..."
                rows={3}
                disabled={submitting}
              />
            </FormField>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-sm">
                    progress_activity
                  </span>
                  Adding...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-sm">add</span>
                  Add Material
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

